<template>
  <zg-form-element
    :hidden="type=== 'hidden'"
    :no-margin="noMargin"
    :full-width="fullWidth"
  >
    <div
      :class="['zg-input', {
        'zg-input--has-label': label,
        'zg-input--has-value': hasValue,
        'zg-input--has-warning': showRecommendedEmail,
        'zg-input--is-typing': hasValue && typing,
        'zg-input--is-valid': isValid,
        'zg-input--is-invalid': invalid,
        'zg-input--is-focused': focused,
        'zg-input--is-disabled': disabled,
        'zg-input--is-read-only': readOnly,
        'zg-input--has-tooltip': tooltip && !disabled,
        'zg-input--has-tooltip-trigger': tooltip && tooltip.trigger && !disabled,
        'zg-input--has-logo': logo
      }]"
    >
      <input
        :id="name"
        v-bind="attrs"
        ref="input"
        :class="{ 'visually-hidden': lookup.visible }"
        :autocomplete="autocomplete"
        :name="name"
        :value="value"
        :type="type"
        :placeholder="lookup.visible ? '' : label"
        :disabled="disabled || readOnly"
        :list="listName"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
        @keyup="onKeyup"
      >

      <img
        v-if="logo"
        :src="logo"
        class="zg-input__logo"
      >

      <zg-input-label
        v-if="label"
        :small="hasValue || focused"
        :name="name"
        :valid="isValid"
        :required="showRequired"
        :hidden="lookup.visible"
      >
        {{ label }}
      </zg-input-label>

      <zg-tooltip
        v-if="tooltip && !disabled && !lookup.visible"
        :title="tooltip.title"
        :text="tooltip.text"
        :visible="focused"
        :hide-trigger="!tooltip.trigger"
        input
      />

      <zg-lookup
        v-if="lookup"
        :label="lookup.label"
        :visible="lookup.visible"
        @toggleLookup="toggle"
      />

      <span
        v-if="unit"
        :class="['zg-input__unit', {
          'zg-input__unit--has-tooltip-trigger': tooltip && tooltip.trigger
        }]"
      >
        {{ unit }}
      </span>

      <template v-if="list">
        <datalist :id="listName">
          <option
            v-for="item in list"
            :key="item"
          >
            {{ item }}
          </option>
        </datalist>

        <span
          :class="[
            'zg-input__list-icon',
            `zg-input__list-icon-${listIcon}`
          ]"
          @click="$emit('resetValue')"
        >
          <zg-icon
            :icon="listIcon"
            size="small"
          />
        </span>
      </template>
    </div>

    <zg-form-error
      :visible="error && invalid"
      type="error"
    >
      {{ error }}
    </zg-form-error>

    <zg-form-error
      :visible="showRecommendedEmail"
      type="warning"
    >
      <span>
        {{ emailWarningMessage }}
        <span
          class="email-recommend"
          @click="onEmailRecommendClick"
        >
          {{ recommendedEmail }}
        </span>
        ?
      </span>
    </zg-form-error>
  </zg-form-element>
</template>

<script>
  import mailcheck from 'mailcheck'
  import ZgFormElement from '../ZgFormElement/ZgFormElement'
  import ZgFormError from '../ZgFormError/ZgFormError.vue'
  import ZgIcon from '../ZgIcon/ZgIcon.vue'
  import ZgInputLabel from '../ZgInputLabel/ZgInputLabel.vue'
  import ZgLookup from '../ZgLookup/ZgLookup.vue'
  import ZgTooltip from '../ZgTooltip/ZgTooltip.vue'
  import mailcheckSettings from './mailcheckSettings.json'

  export default {
    name: 'ZgInput',
    components: {
      ZgFormElement,
      ZgInputLabel,
      ZgLookup,
      ZgTooltip,
      ZgIcon,
      ZgFormError
    },
    props: {
      name: {
        type: String,
        required: true
      },
      label: {
        type: String,
        required: false,
        default: null
      },
      type: {
        type: String,
        required: false,
        default: 'text',
        validator: value => ['text', 'tel', 'email', 'number', 'password'].includes(value)
      },
      value: {
        type: [String, Number],
        required: false,
        default: undefined
      },
      valid: {
        type: Boolean,
        required: false,
        default: false
      },
      invalid: {
        type: Boolean,
        required: false,
        default: false
      },
      error: {
        type: String,
        required: false,
        default: null
      },
      tooltip: {
        type: Object,
        required: false,
        default: null
      },
      attrs: {
        type: Array,
        required: false,
        default: null
      },
      autocomplete: {
        type: String,
        required: false,
        default: 'on',
        validator: value => ['on', 'off'].includes(value)
      },
      emailWarningLanguage: {
        type: String,
        required: false,
        default: null,
        validator: value => ['se', 'no', 'fi', 'en'].includes(value)
      },
      unit: {
        type: String,
        required: false,
        default: null
      },
      logo: {
        type: String,
        required: false,
        default: null
      },
      lookup: {
        type: Object,
        required: false,
        default: () => {
          return { label: '', visible: false }
        },
        validator: (val) => {
          const reqValues = ['label', 'visible'] // valid props

          const matches = reqValues.filter(el => {
            return Object.keys(val).indexOf(el) !== -1
          })

          const label = typeof (val.label) === 'string'
          const visible = typeof (val.visible) === 'boolean'
          const validLengths = matches.length === reqValues.length && Object.keys(val).length === reqValues.length

          return validLengths && label && visible
        }
      },
      list: {
        type: Array,
        required: false,
        default: null
      },
      showRequired: {
        type: Boolean,
        required: false,
        default: false
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      readOnly: {
        type: Boolean,
        required: false,
        default: false
      },
      noMargin: {
        type: Boolean,
        required: false,
        default: false
      },
      fullWidth: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data: () => ({
      focused: false,
      typing: false,
      timeout: null,
      showRecommendedEmail: false,
      recommendedEmail: null
    }),
    computed: {
      hasValue () {
        return !!(this.value || this.value === 0)
      },
      isValid () {
        return !!(this.valid && !this.invalid && !this.disabled)
      },
      emailWarningMessage () {
        const messages = {
          se: 'Menade du',
          no: 'Mente du',
          fi: 'Tarkoititko',
          en: 'Did you mean'
        }

        return messages[this.emailWarningLanguage]
      },
      listName () {
        return `${this.name}List`
      },
      listIcon () {
        return this.hasValue
          ? 'close'
          : 'search'
      }
    },
    methods: {
      onFocus (e) {
        this.focused = true
        this.$emit('focus', e)
      },
      onBlur (e) {
        if (e.target.required && this.focused && !e.target.checkValidity()) {
          e.target.reportValidity()
          e.preventDefault()
        }

        this.focused = false
        this.$emit('blur', e)

        this.checkMail(this.value)
      },
      onInput (e) {
        this.$emit('input', e.target.value)
      },
      onKeydown (e) {
        this.typing = true

        this.$emit('keydown', e)
      },
      onKeyup () {
        clearTimeout(this.timeout)

        this.timeout = setTimeout(() => {
          this.typing = false
        }, 500)
      },
      toggle () {
        this.$emit('toggleLookup')
        this.lookup.visible = !this.lookup.visible // eslint-disable-line vue/no-mutating-props
      },
      onEmailRecommendClick () {
        this.$emit('input', this.recommendedEmail)
        this.showRecommendedEmail = false
      },
      checkMail (mail) {
        if (this.type !== 'email' || this.invalid || this.error || !this.emailWarningLanguage) return

        this.runMailCheck(this.value, 0)
      },
      runMailCheck (value, index = 0) {
        if (index > 10) return
        const { domains, topLevelDomains, secondLevelDomains } = mailcheckSettings

        mailcheck.run({
          email: value,
          domains,
          topLevelDomains,
          secondLevelDomains,
          suggested: ({ full: recommendedEmail }) => {
            this.recommendedEmail = recommendedEmail
            this.showRecommendedEmail = true
            this.runMailCheck(recommendedEmail, index + 1)
          },
          empty: () => {
            if (index > 0) return

            this.recommendedEmail = null
            this.showRecommendedEmail = false
          }
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-input {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 100%;
    height: input(height);
    background-color: color(white);
    border: input(border-width) solid color(input-border-default);
    border-radius: radius(input);
    cursor: pointer;
    transition: 0.3s ease;

    input {
      @include type-body-m;

      position: relative;
      width: 100%;
      height: 100%;
      color: color(gray-500);
      background-color: transparent;
      border: none;
      padding: 0 input(padding-x);
      margin: 0;
      appearance: none;
      transition: 0.3s ease;
      cursor: text;
      z-index: 1;

      &:focus,
      &:active {
        outline: none;
      }

      &::placeholder {
        opacity: 0;
      }

      &::-webkit-calendar-picker-indicator {
        opacity: 0;
      }

      &::-ms-clear,
      &::-ms-expand {
        display: none;
      }

      &:-webkit-autofill {
        -webkit-text-fill-color: color(gray-500);
        box-shadow: inset 0 0 0 200px transparent;
      }

      &[type="number"] {
        appearance: textfield;

        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          margin: 0;
          appearance: none;
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // unit
  // ? ----------------------------------------------------------------------

  .zg-input__unit {
    color: color(gray-500);
    padding: rhythm(half) rhythm(small) rhythm(half) 0;
    transition: all 0.3s ease;

    &--has-tooltip-trigger {
      padding-right: rhythm(large);
    }
  }

  // ? ----------------------------------------------------------------------
  // logo
  // ? ----------------------------------------------------------------------

  .zg-input__logo {
    width: auto;
    height: input(height) - rem(4px);
    position: absolute;
    left: 0;
    top: 0;
    border-radius: rem(8px) 0 0 rem(8px);
    z-index: 0
  }

  // ? ----------------------------------------------------------------------
  // email
  // ? ----------------------------------------------------------------------

  .email-recommend {
    color: color(brand-500);
    font-weight: font-weight(bold);
    cursor: pointer;
  }

  // ? ----------------------------------------------------------------------
  // list
  // ? ----------------------------------------------------------------------

  .zg-input__list-icon {
    position: absolute;
    display: flex;
    opacity: 0.6;
    right: 0.75rem;

    &-search {
      cursor: text;
      pointer-events: none;
    }

    &-close {
      cursor: pointer;
      z-index: 3;
    }
  }

  // ? ----------------------------------------------------------------------
  // states
  // ? ----------------------------------------------------------------------

  .zg-input:hover {
    border-color: color(input-border-hover);
  }

  .zg-input--has-value,
  .zg-input--is-focused {
    input {
      color: color(input-color);
    }

    &.zg-input--has-label {
      input {
        padding-top: input(padding-y) * 1.5;
      }
    }
  }

  .zg-input--has-logo {
    input {
      padding-left: rem(36px);
    }

    label {
      left: rem(36px);
    }
  }

  .zg-input--is-focused {
    border-color: color(input-border-focus);
    box-shadow: box-shadow(focus);

    &:hover {
      border-color: color(input-border-hover);
    }
  }

  .zg-input--is-typing {
    border-color: color(input-color);

    &:hover {
      border-color: color(input-border-hover);
    }
  }

  .zg-input--is-valid {
    border-color: color(input-border-valid);

    &:hover {
      border-color: color(input-border-hover);
    }
  }

  .zg-input--is-invalid {
    border-color: color(input-border-invalid);

    &:hover {
      border-color: color(input-border-invalid);
    }
  }

  .zg-input--is-disabled {
    opacity: 0.5;
    border-color: color(input-border-disabled);

    &:hover {
      border-color: color(input-border-disabled);
    }

    input {
      cursor: not-allowed;
    }
  }

  .zg-input--is-read-only {
    border-color: color(input-border-disabled);

    &:hover {
      border-color: color(input-border-disabled);
    }

    input {
      cursor: not-allowed;
    }
  }

  // ? ----------------------------------------------------------------------
  // tooltip
  // ? ----------------------------------------------------------------------

  .zg-input--has-tooltip-trigger {
    input {
      padding-right: input(tooltip-width);
    }
  }
</style>
